// src/utils/extratoSimplificadoPdf.js
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Cinza escuro no cabeçalho da tabela: o texto branco fica legível mesmo em impressão P&B
const GRAY = [60, 60, 60];
const LINE = [90, 90, 90];
const LIGHT = [244, 244, 244];

const fmtInt = (v) => Math.round(Number(v) || 0).toLocaleString("pt-BR");
const fmt2 = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Função auxiliar para formatar datas ISO (ex: 20270620T000000.000) ou pt-BR
const dataCurta = (s) => {
  if (!s) return "—";
  const str = String(s).trim();

  // YYYYMMDD ou YYYYMMDDTHHMMSS
  const matchISO = str.match(/^(\d{4})(\d{2})(\d{2})/);
  if (matchISO && matchISO[1] >= "1900" && matchISO[1] <= "2100") {
    return `${matchISO[3]}/${matchISO[2]}/${matchISO[1]}`;
  }

  // Fallback para o padrão antigo (DD/MM/YYYY)
  return str.replace(/^(\d{2})\/(\d{2})\/\d{2}(\d{2})/, "$1/$2/$3");
};

// Carrega a logo de /public/images e devolve { data, w, h } (ou null se falhar)
async function carregarLogo(url) {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const blob = await r.blob();
    const data = await new Promise((res) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.readAsDataURL(blob);
    });
    const dim = await new Promise((res) => {
      const img = new Image();
      img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => res(null);
      img.src = data;
    });
    return dim ? { data, ...dim } : null;
  } catch {
    return null;
  }
}

function cabecalho(doc, titulo, ctx, logo) {
  if (logo) {
    const w = 32;
    doc.addImage(logo.data, "PNG", 20, 30, w, (w * logo.h) / logo.w);
  }

  doc.setTextColor(0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(16);
  doc.text(titulo, 133, 34);

  // Cliente / Data (esquerda)
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("Cliente", 20, 62);
  doc.setFont("helvetica", "normal");
  doc.text(ctx.clienteNome || "", 20, 66);
  doc.text(ctx.clienteEmail ? `(${ctx.clienteEmail})` : "", 20, 70);
  doc.setFont("helvetica", "bold");
  doc.text("Data", 20, 76);
  doc.setFont("helvetica", "normal");
  doc.text(ctx.hoje, 20, 80);

  // Gestor (direita)
  doc.text(`Gestor: ${ctx.gestor || ""}`, 133, 62);
  if (ctx.gestorEmail) doc.text(ctx.gestorEmail, 133, 66);
  if (ctx.gestorTelefone) doc.text(ctx.gestorTelefone, 133, 70);

  if (titulo === "Utilização" && ctx.periodo) {
    doc.setFont("helvetica", "bold");
    doc.text("Período de Apuração", 133, 76);
    doc.setFont("helvetica", "normal");
    doc.text(ctx.periodo, 133, 80);
  }
}

const baseTable = (doc, titulo, ctx, logo) => ({
  margin: { top: 88, left: 20, right: 20, bottom: 25 },
  startY: 88,
  theme: "grid",
  styles: { fontSize: 8, cellPadding: 1.8, lineColor: LINE, lineWidth: 0.15, textColor: 0, valign: "middle" },
  headStyles: { fillColor: GRAY, textColor: 255, fontStyle: "normal", lineWidth: 0 },
  didDrawPage: () => cabecalho(doc, titulo, ctx, logo),
});

/**
 * @param {object} p
 * @param {object} p.cliente        { nome, email }
 * @param {string} p.gestor         nome do gestor
 * @param {string} [p.gestorEmail]
 * @param {string} [p.gestorTelefone]
 * @param {Array}  p.titulares      [{ cli_id, cli_nome }]
 * @param {Array}  p.cartoes        [{ cli_id, car_nome_programa, car_saldo_milhas, car_vunitario, car_qtde_expira, car_dtexpiracao, car_expira }]
 * @param {Array}  p.utilizacao     [{ descricao, data, lote, cm, pagante, extras, preco, economia, pct }]
 * @param {string} [p.comunicados]  texto da página de Comunicados
 * @param {string} [p.periodo]      ex.: "20/02/2026 - 20/02/2027"
 * @param {string} [p.logoUrl]      default "/images/logo.png"
 */
export async function gerarExtratoSimplificado(p) {
  const logo = await carregarLogo(p.logoUrl || "/images/logo.png");
  const hoje = new Date().toLocaleDateString("pt-BR");
  const ctx = {
    clienteNome: p.cliente?.nome,
    clienteEmail: p.cliente?.email,
    gestor: p.gestor,
    gestorEmail: p.gestorEmail,
    gestorTelefone: p.gestorTelefone,
    periodo: p.periodo,
    hoje,
  };

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const titulares = p.titulares?.length ? p.titulares : [{ cli_id: null, cli_nome: p.cliente?.nome }];
  const cartoesDe = (t) =>
    p.cartoes.filter((c) => String(c.cli_id) === String(t.cli_id) || t.cli_id === null);

  // ── Página 1: Extrato de Contas ──────────────────────────────────────────
  {
    const body = [];
    let total = 0;
    titulares.forEach((t) => {
      body.push([{ content: t.cli_nome || "Titular", colSpan: 4, styles: { fontStyle: "bold", fillColor: LIGHT, fontSize: 9 } }]);
      cartoesDe(t).forEach((c) => {
        const patr = ((Number(c.car_saldo_milhas) || 0) * (Number(c.car_vunitario) || 0)) / 1000;
        total += patr;
        body.push([
          { content: c.car_nome_programa || "—", styles: { cellPadding: { top: 1.8, bottom: 1.8, left: 7, right: 1.8 } } },
          fmtInt(c.car_saldo_milhas),
          fmt2(c.car_vunitario),
          fmt2(patr),
        ]);
      });
    });
    body.push([
      { content: "Patrimônio total", colSpan: 3, styles: { fontStyle: "bold", fillColor: LIGHT, fontSize: 9 } },
      { content: fmt2(total), styles: { fontStyle: "bold", fillColor: LIGHT, halign: "right", fontSize: 9 } },
    ]);

    autoTable(doc, {
      ...baseTable(doc, "Extrato de Contas", ctx, logo),
      head: [["Titular/Conta", "Saldo", "Custo Médio", "Patrimônio"]],
      body,
      columnStyles: {
        0: { cellWidth: 69 },
        1: { cellWidth: 34, halign: "right" },
        2: { cellWidth: 34, halign: "right" },
        3: { cellWidth: 33, halign: "right" },
      },
      didParseCell: (d) => {
        if (d.section === "head" && d.column.index > 0) d.cell.styles.halign = "right";
      },
    });
  }

  // ── Página 2: Lotes a Expirar ────────────────────────────────────────────
  doc.addPage();
  {
    const body = [];
    titulares.forEach((t) => {
      body.push([{ content: t.cli_nome || "Titular", colSpan: 4, styles: { fontStyle: "bold", fillColor: LIGHT, fontSize: 9 } }]);
      cartoesDe(t).forEach((c) => {
        const expira = c.car_expira === 1 || c.car_expira === "1";
        const q = Number(c.car_qtde_expira) || 0;

        let lote1 = "---";
        if (expira && q > 0 && c.car_dtexpiracao) {
          const valorFormatado = `${fmtInt(q)} em`;
          const dataFormatada = dataCurta(c.car_dtexpiracao);
          lote1 = `${valorFormatado}\n${dataFormatada}`;
        }

        body.push([
          { content: c.car_nome_programa || "—", styles: { cellPadding: { top: 1.8, bottom: 1.8, left: 7, right: 1.8 } } },
          lote1,
          "---",
          "---",
        ]);
      });
    });

    autoTable(doc, {
      ...baseTable(doc, "Lotes a Expirar", ctx, logo),
      head: [["Titular/Conta", "1º Lote a Expirar", "2º Lote a Expirar", "3º Lote a Expirar"]],
      body,
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 40, halign: "right" },
        2: { cellWidth: 38, halign: "right" },
        3: { cellWidth: 37, halign: "right" },
      },
      didParseCell: (d) => {
        if (d.section === "head" && d.column.index > 0) d.cell.styles.halign = "right";
        if (d.section === "body" && d.column.index > 0) {
          d.cell.text = d.cell.text.map((t) => String(t));
        }
      },
    });
  }

  // ── Página 3: Utilização ─────────────────────────────────────────────────
  doc.addPage();
  {
    const util = p.utilizacao || [];
    const totalEcon = util.reduce((s, r) => s + (Number(r.economia) || 0), 0);

    // PRÉ-PROCESSAMENTO: monta o texto de economia+pct ANTES de chamar autoTable.
    // Isso evita o bug de didDrawCell desenhar no lugar errado quando quebra página.
    const body = util.map((r) => {
      const econStr = fmt2(r.economia);
      const pctStr = `${fmt2(r.pct)}%`;
      return [
        r.descricao,
        dataCurta(r.data),
        fmtInt(r.lote),
        fmt2(r.cm),
        fmt2(r.pagante),
        fmt2(r.extras),
        fmt2(r.preco),
        // Usa string multilinha: economia em cima, pct embaixo
        `${econStr}\n${pctStr}`,
      ];
    });
    body.push([
      { content: "Economia total", colSpan: 7, styles: { fontSize: 9, fillColor: LIGHT, fontStyle: "bold" } },
      { content: fmt2(totalEcon), styles: { fontStyle: "bold", halign: "right", fontSize: 9, fillColor: LIGHT } },
    ]);

    autoTable(doc, {
      ...baseTable(doc, "Utilização", ctx, logo),
      head: [["Utilização", "Data", "Lote", "CM", "Pagante", "Extras", "Preço", "Economia"]],
      body,
      styles: { ...baseTable(doc, "", ctx, logo).styles, fontSize: 7.5, overflow: "linebreak" },
      columnStyles: {
        0: { cellWidth: 44 },
        1: { cellWidth: 16 },
        2: { cellWidth: 20, halign: "right" },
        3: { cellWidth: 11, halign: "right" },
        4: { cellWidth: 22, halign: "right" },
        5: { cellWidth: 17, halign: "right" },
        6: { cellWidth: 16, halign: "right" },
        7: { cellWidth: 24, halign: "right" },
      },
      didParseCell: (d) => {
        if (d.section === "head" && d.column.index >= 2) d.cell.styles.halign = "right";
        // Coluna Economia: preto (sem cor), negrito e fonte maior para impressão
        if (d.section === "body" && d.column.index === 7) {
          d.cell.styles.fontSize = 7.5;
          d.cell.styles.textColor = 0;
          d.cell.styles.fontStyle = "bold";
        }
      },
    });
  }

  // ── Página 4: Comunicados ────────────────────────────────────────────────
  doc.addPage();
  cabecalho(doc, "Comunicados", ctx, logo);
  if (p.comunicados?.trim()) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(doc.splitTextToSize(p.comunicados, 170), 20, 92);
  }

  // ── Rodapé em todas as páginas ───────────────────────────────────────────
  const n = doc.internal.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i);
    doc.setTextColor(0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Extrato de Contas", 14, 277);
    doc.setFontSize(6);
    doc.text("emitido por ADM Milhas", 14, 280);
    doc.setFontSize(9);
    doc.text(hoje, 197, 277, { align: "right" });
  }

  const nome = (p.cliente?.nome || "Cliente").replace(/\s+/g, "_");
  doc.save(`extrato_simplificado_${nome}_${new Date().toISOString().slice(0, 10)}.pdf`);
}